/**
 * TRL team colour palette picker.
 *
 * Colours are unique per game — a team's colour is also its Discord role colour,
 * so two teams sharing one would be indistinguishable in the server list. Taken
 * colours are therefore shown but disabled, which tells an admin *why* a colour is
 * unavailable (the tooltip names the owning team) rather than silently hiding it.
 */

import { useMemo } from 'react';
import { TRL_TEAM_COLORS, type TrlTeam } from '@swtorbit/shared-socket-events';

interface TeamColorGridProps {
  teams: TrlTeam[];
  selected: string | undefined;
  /** The team being recoloured (its own colour stays selectable); omit when creating. */
  ownTeamId?: string;
  onSelect: (color: string) => void;
}

export function TeamColorGrid({ teams, selected, ownTeamId, onSelect }: TeamColorGridProps) {
  const takenBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teams) {
      if (t.color && t.id !== ownTeamId) map.set(t.color.toLowerCase(), t.name);
    }
    return map;
  }, [teams, ownTeamId]);

  return (
    <div className="d-grid gap-2xs" style={{ gridTemplateColumns: 'repeat(5, max-content)' }}>
      {TRL_TEAM_COLORS.map((color) => {
        const owner = takenBy.get(color);
        const isSelected = selected?.toLowerCase() === color;
        return (
          <button
            key={color}
            type="button"
            className={`icon icon--2xl p-0 rounded-sm border-xl ${isSelected ? 'border-white' : 'border-space'}`}
            style={{
              background: color,
              opacity: owner ? 0.2 : 1,
              cursor: owner ? 'not-allowed' : 'pointer',
            }}
            disabled={!!owner}
            title={owner ? `Used by ${owner}` : color}
            aria-label={owner ? `${color} (used by ${owner})` : color}
            aria-pressed={isSelected}
            onClick={() => onSelect(color)}
          />
        );
      })}
    </div>
  );
}

export default TeamColorGrid;
